import { FC, useContext } from 'react'
import { Grid, Typography, Paper, Box, InView, Stack } from '@hermes/web-components'
import dynamic from 'next/dynamic'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { PracticeProfile } from '../../../../types/practice'
import { DayAvailabilityRoot, Wrapper, themedColor } from '../../styles'
import { useWpaTenant } from '../../../../hooks/useWpaTenant'
import { wpaMarkerIndicator, inactiveMarkerIndicator } from '../../../../constants/global'
import { transformAddressToString } from '../../../../utils/address'
import { AppDataContext } from '../../../../providers/AppData'

const Marker = dynamic(() => import('@react-google-maps/api').then((module) => module.Marker), { ssr: false })
const GoogleMap = dynamic(() => import('../../../GoogleMap'), { ssr: false })

export type PracticeLocationsProps = {
  workingHours: any | null
  address: PracticeProfile['address']
  hideGetInTouch?: boolean
  defaultAddress?: { lat: number; lon: number }
  isAvailable: boolean
}

type DayAvailabilityProps = {
  day: string
  hours?: { open: string; close: string }[]
  hideGetInTouch?: boolean
  isAvailable: boolean
}

const workingDays: Record<string, number> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7
}

const DayAvailability: FC<DayAvailabilityProps> = ({ day, hours, hideGetInTouch, isAvailable }) => {
  const translate = useTranslateMessage()

  const primaryText = 'text.primary'

  const isWorking = !!hours?.length
  const textFontWeight = isWorking ? '600' : '400'
  const textColor = isWorking ? primaryText : 'text.disabled'

  return (
    <DayAvailabilityRoot alignItems="center">
      <Box>
        <Typography color={primaryText} fontWeight={'600'} variant="body2" textTransform="capitalize">
          {translate(day)}
        </Typography>
      </Box>
      <Box>
        {isWorking ? (
          hours.map((hour) => {
            const workingTime = `${hour.open} - ${hour.close}`

            return (
              <Typography key={`${day}-${workingTime}`} fontWeight={textFontWeight} variant="body2" color={textColor}>
                {workingTime}
              </Typography>
            )
          })
        ) : (
          <Typography color={hideGetInTouch ? 'text.disabled' : primaryText} fontWeight="400" variant="body2">
            {translate(!isAvailable ? 'not_available' : 'global.enquire')}
          </Typography>
        )}
      </Box>
    </DayAvailabilityRoot>
  )
}

const PracticeLocations: FC<PracticeLocationsProps> = ({
  workingHours,
  address,
  hideGetInTouch,
  defaultAddress = { lat: 0, lon: 0 },
  isAvailable = false
}) => {
  const { language } = useContext(AppDataContext)

  const translate = useTranslateMessage()
  const { locationTitleColor } = themedColor()
  const { isWPA } = useWpaTenant()

  const transformedAddress = transformAddressToString(address, language)

  return (
    <Box sx={{ width: '100%' }}>
      <Typography mb="16px" fontSize="30px" lineHeight="40px" variant="h2" component="h2" color={locationTitleColor}>
        {translate('locations')}
      </Typography>
      <Paper sx={{ p: 2, boxShadow: 'none' }}>
        <Wrapper container rowSpacing={3} columnSpacing={5}>
          <Grid item lg={6} xs={12}>
            <Typography mb="16px" fontSize="15px" lineHeight="20px" component="h3" fontWeight={500}>
              {translate('working_hours')}
            </Typography>
            {Object.keys(workingDays).map((workingDay) => (
              <DayAvailability
                key={`practice-working-day-${workingDay}`}
                day={workingDay}
                hours={workingHours?.[workingDays[workingDay]]}
                hideGetInTouch={hideGetInTouch}
                isAvailable={isAvailable}
              />
            ))}
          </Grid>
          <Grid item lg={6} xs={12}>
            <Stack height="100%">
              <Box flex={1}>
                <InView triggerOnce>
                  {({ ref, inView }) => (
                    <Box ref={ref} sx={{ height: { lg: '100%', sm: 300, xs: 204 } }}>
                      {inView && (
                        <GoogleMap
                          center={{
                            lat: address.geolocation?.lat || defaultAddress.lat,
                            lng: address.geolocation?.lon || defaultAddress.lon
                          }}
                          mapContainerStyle={{ width: '100%', height: '100%' }}
                          options={{ fullscreenControl: false, mapTypeControl: false }}
                        >
                          <Marker
                            position={{
                              lat: address.geolocation?.lat,
                              lng: address.geolocation?.lon
                            }}
                            icon={isWPA ? wpaMarkerIndicator : inactiveMarkerIndicator}
                          />
                        </GoogleMap>
                      )}
                    </Box>
                  )}
                </InView>
              </Box>
              <Typography
                fontSize="11px"
                lineHeight="14px"
                mt={{ xs: '16px', lg: '8px' }}
                sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', flexShrink: 0 }}
              >
                {transformedAddress}
              </Typography>
            </Stack>
          </Grid>
        </Wrapper>
      </Paper>
    </Box>
  )
}

export default PracticeLocations
